import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Chip,
} from '@mui/material';
import { Plus as AddIcon } from 'lucide-react';
import { TeamMember } from '../../types';

interface TeamViewProps {
  teamMembers: TeamMember[];
  teamMemberFilter: 'all' | 'available' | 'unavailable';
  setTeamMemberFilter: (filter: 'all' | 'available' | 'unavailable') => void;
  handleDeleteTeamMember: (memberId: number) => void;
}

const TeamView: React.FC<TeamViewProps> = ({
  teamMembers,
  teamMemberFilter,
  setTeamMemberFilter,
  handleDeleteTeamMember,
}) => {
  const filteredMembers = teamMembers.filter(member => {
    if (teamMemberFilter === 'available') return member.status === 'Available';
    if (teamMemberFilter === 'unavailable') return member.status === 'Unavailable';
    return true;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ 
          color: '#2c2c2c', 
          fontWeight: 'bold',
          fontSize: { xs: '1.5rem', md: '2.125rem' }
        }}>
          Team Members
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#f59f1f',
            color: 'white',
            borderRadius: '50%',
            minWidth: { xs: '40px', md: '48px' },
            width: { xs: '40px', md: '48px' },
            height: { xs: '40px', md: '48px' },
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'none',
            '&:hover': { 
              backgroundColor: '#e08f0f'
            }
          }}
          disabled
          title="Add Team Member"
        >
          <AddIcon size={20} />
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: '#666', mb: 3, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Manage your organization's people. Add, edit, or remove team members and view their availability.
      </Typography>

      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 2 },
          overflowX: { xs: 'auto', md: 'visible' },
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}>
          {(['all', 'available', 'unavailable'] as const).map((filter) => (
            <Button
              key={filter}
              onClick={() => setTeamMemberFilter(filter)}
              sx={{
                pb: 1,
                px: { xs: 1.5, md: 2 },
                borderRadius: 0,
                borderBottom: teamMemberFilter === filter ? '3px solid #f59f1f' : '3px solid transparent',
                color: teamMemberFilter === filter ? '#f59f1f' : '#666',
                fontWeight: teamMemberFilter === filter ? 'bold' : 'normal',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: 'nowrap',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(245, 159, 31, 0.05)' }
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)} ({
                filter === 'all' ? teamMembers.length : 
                teamMembers.filter(m => m.status === (filter === 'available' ? 'Available' : 'Unavailable')).length
              })
            </Button>
          ))}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ 
        overflow: 'hidden', 
        boxShadow: 'none', 
        border: '1px solid #e0e0e0',
        display: { xs: 'none', md: 'block' }
      }}>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#2c2c2c' }}>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Name</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Role</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Department</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Location</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Email</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Phone</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member, index) => (
                <tr 
                  key={member.id}
                  style={{ 
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <td style={{ padding: '16px' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2c2c2c' }}>
                      {member.firstName} {member.lastName}
                    </Typography>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <Typography variant="body2">{member.role}</Typography>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <Typography variant="body2">{member.department}</Typography>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <Typography variant="body2">{member.location}</Typography>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <Chip
                      label={member.status}
                      size="small"
                      sx={{
                        backgroundColor: 'transparent',
                        color: '#333',
                        fontWeight: 'normal',
                        fontSize: '0.75rem',
                        border: '1px solid #ddd'
                      }}
                    />
                  </td>
                  <td style={{ padding: '16px' }}>
                    <Typography variant="body2">{member.email}</Typography>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <Typography variant="body2">{member.phone}</Typography>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button size="small" onClick={() => handleDeleteTeamMember(member.id)} sx={{ color: '#d32f2f' }}>Delete</Button>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      
      {/* Mobile View Placeholder */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {filteredMembers.map((member) => (
          <Paper key={member.id} elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {member.firstName} {member.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.role} • {member.department}
                </Typography>
              </Box>
              <Chip label={member.status} size="small" variant="outlined" />
            </Box>
            <Typography variant="body2" sx={{ mb: 1 }}>{member.email}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteTeamMember(member.id)} fullWidth>Delete</Button>
            </Box>
          </Paper>
        ))}
      </Box>
    </Container>
  );
};

export default TeamView;
